import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapIcon, UtensilsCrossedIcon, ClockIcon } from 'lucide-react';
export function SolutionSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="w-full bg-cream py-16 md:py-24" ref={ref} data-id="element-238">
      <div className="max-w-prose mx-auto px-5" data-id="element-239">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8
      }} data-id="element-240">
          <p className="font-sans text-xs tracking-[0.15em] uppercase text-sage mb-4" data-id="element-241">
            La solución
          </p>

          <h2 className="font-sans font-bold text-2xl md:text-3xl text-charcoal mb-4 leading-tight" data-id="element-242">
            Anti-Hinchazón 14 Días{' '}
            <span className="text-terracotta" data-id="element-243">(Peri 30+)</span>
          </h2>

          <p className="font-serif text-lg md:text-xl leading-[1.85] text-charcoal/90 mb-8" data-id="element-244">
            Una guía sencilla, sin lácteos, con medidas USA, diseñada para
            mujeres que no tienen tiempo para vivir a dieta… pero ya no aguantan
            vivir infladas.
          </p>
        </motion.div>

        {/* Product Image */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={isInView ? {
        opacity: 1,
        scale: 1
      } : {}} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="my-10 md:my-14" data-id="element-245">
          <img src="https://cdn.magicpatterns.com/uploads/iP6NGGeVRVLCizGSZyJUhb/anti-hinchazon_de_14_dias.png_imagen.png" alt="Anti-Hinchazón 14 Días (Peri 30+) — Guía en tablet con alimentos saludables" className="w-full max-w-lg mx-auto rounded-2xl shadow-xl shadow-charcoal/10" data-id="element-246" />
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.8,
        delay: 0.35
      }} className="space-y-6" data-id="element-247">
          <p className="font-serif text-lg md:text-xl leading-[1.85] text-charcoal/90" data-id="element-248">
            Lo que hace diferente este plan: no te pide "hacer más". Te pide{' '}
            <strong data-id="element-249">hacer menos, pero detectable.</strong>
          </p>

          <p className="font-sans font-semibold text-lg text-charcoal mb-6" data-id="element-250">
            Vas a usar: El Mapa de Disparadores Invisibles + 3 Swaps
          </p>

          {/* The 3 Swaps */}
          <div className="space-y-4" data-id="element-251">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.45
          }} className="bg-warm rounded-xl p-5 md:p-6 border border-gold/20" data-id="element-252">
              <div className="flex items-start gap-4" data-id="element-253">
                <div className="w-10 h-10 rounded-lg bg-sage/15 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-254">
                  <MapIcon className="w-5 h-5 text-sage" data-id="element-255" />
                </div>
                <div data-id="element-256">
                  <p className="font-sans font-semibold text-charcoal mb-1" data-id="element-257">
                    Identificas los detonantes típicos
                  </p>
                  <p className="font-serif text-base text-charcoal/70 leading-relaxed" data-id="element-258">
                    Sin entrar en paranoia. 7 disparadores comunes que
                    probablemente no sabías que te afectan.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.55
          }} className="bg-warm rounded-xl p-5 md:p-6 border border-gold/20" data-id="element-259">
              <div className="flex items-start gap-4" data-id="element-260">
                <div className="w-10 h-10 rounded-lg bg-terracotta/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-261">
                  <UtensilsCrossedIcon className="w-5 h-5 text-terracotta" data-id="element-262" />
                </div>
                <div data-id="element-263">
                  <p className="font-sans font-semibold text-charcoal mb-1" data-id="element-264">
                    3 sustituciones simples
                  </p>
                  <div className="font-serif text-base text-charcoal/70 leading-relaxed space-y-1" data-id="element-265">
                    <p data-id="element-266">
                      <span className="font-sans text-sm font-medium text-terracotta" data-id="element-267">
                        1.
                      </span>{' '}
                      Desayuno que no fermente
                    </p>
                    <p data-id="element-268">
                      <span className="font-sans text-sm font-medium text-terracotta" data-id="element-269">
                        2.
                      </span>{' '}
                      Plato "3 piezas" (simple, repetible)
                    </p>
                    <p data-id="element-270">
                      <span className="font-sans text-sm font-medium text-terracotta" data-id="element-271">
                        3.
                      </span>{' '}
                      Snack estable (anti-ansiedad, sin explosión)
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.65
          }} className="bg-warm rounded-xl p-5 md:p-6 border border-gold/20" data-id="element-272">
              <div className="flex items-start gap-4" data-id="element-273">
                <div className="w-10 h-10 rounded-lg bg-gold/15 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-274">
                  <ClockIcon className="w-5 h-5 text-gold" data-id="element-275" />
                </div>
                <div data-id="element-276">
                  <p className="font-sans font-semibold text-charcoal mb-1" data-id="element-277">
                    Timing + rutina mínima
                  </p>
                  <p className="font-serif text-base text-charcoal/70 leading-relaxed" data-id="element-278">
                    15 minutos de caminata y horarios que desinflan. Sin
                    entrenos extremos.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}